import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Plus, Search, Loader2, Car, Trash2, Pencil, FileDown,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown, X,
} from 'lucide-react';
import { API } from '../../../lib/api';
import { notifyErr } from '../../../components/admin/billing/shared';

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
};

const STATUS_OPTIONS = ['draft', 'in_progress', 'completed', 'cancelled'];

const gradeChip = (grade) => {
  const map = {
    Excellent: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
    Good: 'bg-teal-500/15 text-teal-300 border-teal-500/40',
    Average: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
    Poor: 'bg-red-500/15 text-red-300 border-red-500/40',
  };
  const cls = map[grade] || 'bg-slate-500/15 text-slate-300 border-slate-500/40';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider border rounded px-2 py-0.5 ${cls}`}>
      {grade || 'Pending'}
    </span>
  );
};

const UsedCarInspections = () => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); },
    [debouncedSearch, status, fromDate, toDate, sortBy, sortDir, pageSize]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir,
      };
      if (debouncedSearch) params.q = debouncedSearch;
      if (status) params.status = status;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await axios.get(`${API}/used-car-inspections`, {
        params, headers: authHeader(),
      });
      const rows = Array.isArray(data) ? data : (data?.rows || []);
      setItems(rows);
      setTotal(Array.isArray(data) ? rows.length : (data?.total ?? rows.length));
      setPages(Array.isArray(data) ? 1 : (data?.pages ?? 1));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [authHeader, debouncedSearch, status, fromDate, toDate,
      sortBy, sortDir, page, pageSize]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir('desc'); }
  };
  const clearFilters = () => {
    setSearch(''); setStatus(''); setFromDate(''); setToDate('');
    setSortBy('created_at'); setSortDir('desc');
  };
  const filtersActive = !!(debouncedSearch || status || fromDate || toDate);
  const startIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);

  const onDelete = async (id, label) => {
    if (!window.confirm(`Delete inspection "${label}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/used-car-inspections/${id}`, { headers: authHeader() });
      fetchAll();
    } catch (e) {
      notifyErr(e);
    }
  };

  const onDownloadPdf = async (it) => {
    try {
      const res = await axios.get(`${API}/used-car-inspections/${it.id}/pdf`, {
        headers: authHeader(), responseType: 'blob', timeout: 60000,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `UsedCarInspection_${it.booking_id || it.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 800);
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Failed to download PDF');
    }
  };

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <div className="flex items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="hidden lg:flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-teal-500/15 border border-teal-500/30 flex items-center justify-center flex-shrink-0">
              <Car className="text-teal-300" size={18} />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="used-car-inspections-title">Used Car Inspections</h1>
              <p className="text-sm text-slate-500 mt-0.5">Detailed Pre Purchase reports · Auto-graded PDF</p>
            </div>
          </div>
          <div className="lg:hidden" />
          <button
            onClick={() => navigate('/admin/used-car-inspections/new')}
            className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm px-3 sm:px-4 py-2 rounded-lg transition flex-shrink-0 ml-auto"
            data-testid="new-used-car-inspection-btn"
          >
            <Plus size={16} /> <span className="hidden xs:inline">New </span>Inspection
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by booking id, make, model, registration, owner, phone, email, inspector…"
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              data-testid="used-car-inspections-search"
            />
          </div>
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-3 py-2.5 bg-white/5 border border-white/10 hover:bg-white/10 rounded-lg text-sm text-slate-300"
              data-testid="clear-filters"
            >
              <X size={13} /> Clear
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">From</span>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-slate-200"
              data-testid="from-date" />
            <span className="text-slate-500">To</span>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-slate-200"
              data-testid="to-date" />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-slate-500 mr-1">Status</span>
            <button type="button" onClick={() => setStatus('')}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${status === '' ? 'bg-teal-500/20 border-teal-400 text-teal-200' : 'bg-white/5 border-white/10 text-slate-400 hover:text-slate-200'}`}
              data-testid="status-all">All</button>
            {STATUS_OPTIONS.map((s) => (
              <button key={s} type="button"
                onClick={() => setStatus(s === status ? '' : s)}
                className={`text-xs px-2.5 py-1 rounded-full border capitalize transition ${status === s ? 'bg-teal-500/20 border-teal-400 text-teal-200' : 'bg-white/5 border-white/10 text-slate-400 hover:text-slate-200'}`}
                data-testid={`status-${s}`}>{s.replace('_', ' ')}</button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 text-sm text-slate-400">
            <span>Rows per page:</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
              className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm"
              data-testid="page-size">
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="used-car-inspections-error">
            {String(error)}
          </div>
        )}

        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 border-b border-white/10">
                <tr className="text-left">
                  {[
                    { key: 'created_at', label: 'Date' },
                    { key: 'booking_id', label: 'Booking ID' },
                    { key: 'make', label: 'Vehicle' },
                    { key: 'inspector_name', label: 'Inspector', sortable: false },
                    { key: 'overall_score', label: 'Grade', sortable: false },
                    { key: 'overall_score', label: 'Score' },
                  ].map((col, i) => (
                    <th key={i} className="px-4 py-3">
                      {col.sortable === false ? (
                        <span className="uppercase tracking-wider text-xs text-slate-400">{col.label}</span>
                      ) : (
                        <button type="button" onClick={() => toggleSort(col.key)}
                          className="inline-flex items-center gap-1 uppercase tracking-wider text-xs text-slate-400 hover:text-slate-200"
                          data-testid={`sort-${col.key}`}>
                          {col.label}
                          {sortBy === col.key && (sortDir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                        </button>
                      )}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-400 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400" data-testid="used-car-inspections-loading">
                    <Loader2 className="animate-spin inline mr-2" size={16} /> Loading…
                  </td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400" data-testid="used-car-inspections-empty">
                    {filtersActive
                      ? <>No inspections match these filters. <button onClick={clearFilters} className="text-teal-300 underline">Clear filters</button>.</>
                      : <>No used-car inspections yet. Click <strong className="text-teal-300">New Inspection</strong> to create one.</>}
                  </td></tr>
                )}
                {!loading && items.map((it) => (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`used-car-inspection-row-${it.id}`}>
                    <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(it.inspection_date || it.created_at)}</td>
                    <td className="px-4 py-3 text-slate-300 font-mono text-xs">{it.booking_id || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-100">
                        {[it.make, it.model, it.variant].filter(Boolean).join(' ') || '—'}
                      </div>
                      {it.registration_number && (
                        <div className="text-xs font-mono tracking-wider text-teal-300 mt-0.5">
                          {it.registration_number}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{it.inspector_name || '—'}</td>
                    <td className="px-4 py-3">{gradeChip(it.overall_grade)}</td>
                    <td className="px-4 py-3 text-slate-200 font-semibold">
                      {typeof it.overall_score === 'number' ? `${it.overall_score.toFixed(1)}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => navigate(`/admin/used-car-inspections/${it.id}`)}
                          className="inline-flex items-center gap-1 text-xs bg-teal-500/15 hover:bg-teal-500/25 border border-teal-500/30 text-teal-300 px-2.5 py-1.5 rounded transition"
                          data-testid={`open-used-car-inspection-${it.id}`}
                        >
                          <Pencil size={12} /> Open
                        </button>
                        <button
                          onClick={() => onDownloadPdf(it)}
                          className="inline-flex items-center gap-1 text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 px-2.5 py-1.5 rounded transition"
                          data-testid={`pdf-used-car-inspection-${it.id}`}
                        >
                          <FileDown size={12} /> PDF
                        </button>
                        <button
                          onClick={() => onDelete(it.id, `${it.make || ''} ${it.model || ''}`.trim() || it.booking_id)}
                          className="inline-flex items-center justify-center w-8 h-8 bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-slate-400 hover:text-red-300 rounded transition"
                          data-testid={`delete-used-car-inspection-${it.id}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 text-sm text-slate-400" data-testid="pagination">
            <div>
              {total === 0
                ? 'No results'
                : `${startIdx.toLocaleString()}–${endIdx.toLocaleString()} of ${total.toLocaleString()}`}
            </div>
            <div className="flex items-center gap-1">
              <button type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded disabled:opacity-40"
                data-testid="page-prev">
                <ChevronLeft size={14} /> Prev
              </button>
              <span className="px-3 text-slate-500">Page {page} of {pages || 1}</span>
              <button type="button"
                disabled={page >= pages || loading}
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded disabled:opacity-40"
                data-testid="page-next">
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UsedCarInspections;
